import React from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';

interface DateRangeFilterProps {
  startDate?: string;
  endDate?: string;
  onChange: (startDate?: string, endDate?: string) => void;
}

type PresetRange = 'all' | '1700s' | '1800s' | '1900s' | '2000s' | 'custom';

/**
 * DateRangeFilter Component
 * Filter timeline events by date range with presets and custom dates
 * Part of Phase 3.3: Interactive Timeline Page
 */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const [preset, setPreset] = React.useState<PresetRange>('all');

  const handlePresetChange = (_event: React.MouseEvent<HTMLElement>, newPreset: PresetRange | null) => {
    if (!newPreset) return;

    setPreset(newPreset);

    switch (newPreset) {
      case 'all':
        onChange(undefined, undefined);
        break;
      case '1700s':
        onChange('1700-01-01', '1799-12-31');
        break;
      case '1800s':
        onChange('1800-01-01', '1899-12-31');
        break;
      case '1900s':
        onChange('1900-01-01', '1999-12-31');
        break;
      case '2000s':
        onChange('2000-01-01', '2099-12-31');
        break;
      case 'custom':
        // Keep current dates or clear
        break;
    }
  };

  const handleStartDateChange = (value: string) => {
    setPreset('custom');
    onChange(value || undefined, endDate);
  };

  const handleEndDateChange = (value: string) => {
    setPreset('custom');
    onChange(startDate, value || undefined);
  };

  const handleReset = () => {
    setPreset('all');
    onChange(undefined, undefined);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Date Range
        </Typography>
        {(startDate || endDate) && (
          <Typography variant="caption" color="text.secondary">
            {startDate && startDate.substring(0, 4)} -{' '}
            {endDate ? endDate.substring(0, 4) : 'Present'}
          </Typography>
        )}
      </Box>

      {/* Preset Ranges */}
      <ToggleButtonGroup
        value={preset}
        exclusive
        onChange={handlePresetChange}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        <ToggleButton value="all">All Time</ToggleButton>
        <ToggleButton value="1700s">1700s</ToggleButton>
        <ToggleButton value="1800s">1800s</ToggleButton>
        <ToggleButton value="1900s">1900s</ToggleButton>
        <ToggleButton value="2000s">2000s</ToggleButton>
      </ToggleButtonGroup>

      <Divider sx={{ my: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Custom Range
        </Typography>
      </Divider>

      {/* Custom Date Inputs */}
      <Stack spacing={2}>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          fullWidth
          value={startDate || ''}
          onChange={(e) => handleStartDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="End Date"
          type="date"
          size="small"
          fullWidth
          value={endDate || ''}
          onChange={(e) => handleEndDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        {/* Reset Button */}
        {(startDate || endDate) && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleReset}
          >
            Reset to All Time
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default DateRangeFilter;
